import { useEffect } from 'react';

import { usePreferences } from '@/lib/preferences';
import { useSidebarStore } from './sidebarStore';

/**
 * Applies the person's stored preferences to the running application.
 *
 * This is the bridge that was missing: the values were saved to the account and read by nothing,
 * so Settings could report "compact" while every table stayed comfortable. It renders nothing —
 * it only ensures the document and the shell reflect what the account says.
 *
 * The theme is NOT applied here. It has its own store with a localStorage paint cache, because a
 * colour must be right in the first frame or every reload flashes white before going dark;
 * density and sidebar have no such constraint and can settle when the account answers.
 */
export function PreferenceEffects() {
  const prefs = usePreferences();
  const setCollapsed = useSidebarStore((s) => s.setCollapsed);
  const adoptedSidebar = useSidebarStore((s) => s.adoptedFromAccount);
  const markAdopted = useSidebarStore((s) => s.markAdopted);

  // Density — a `data-density` attribute the stylesheet keys off, so one attribute changes every
  // dense surface at once instead of each component subscribing.
  useEffect(() => {
    document.documentElement.dataset['density'] = prefs.density;
  }, [prefs.density]);

  // Locale and timezone are advertised to the document for assistive technology and for any
  // browser-native formatting; the application's own formatting goes through lib/format.ts.
  useEffect(() => {
    document.documentElement.lang = prefs.effective.locale.split('-')[0] ?? 'en';
  }, [prefs.effective.locale]);

  // The sidebar is collapsed per PERSON, not per browser. It was persisted to localStorage only,
  // so a barrister who collapsed it at chambers found it expanded again at home. The account is
  // adopted once per session; after that the local store leads, so a toggle stays responsive and
  // the account catches up in the background.
  useEffect(() => {
    if (adoptedSidebar || !prefs.initialised) return;
    setCollapsed(prefs.sidebarCollapsed, { persist: false });
    markAdopted();
  }, [adoptedSidebar, prefs.initialised, prefs.sidebarCollapsed, setCollapsed, markAdopted]);

  return null;
}
